import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNowMs } from '@make-software/tg-mini-app-ui';

import {
  useSeasonInfo,
  useHackathonProjects,
  AppDispatch,
  fetchVoteRanking,
  selectVoteRanking
} from '@/shared';
import { IVoteRanking } from '@/entities';

export const useScreen = (documentId?: string) => {
  const dispatch = useDispatch<AppDispatch>();
  const voteRanking = useSelector(selectVoteRanking);

  const { data: airdropInfo } = useSeasonInfo();
  const round = airdropInfo?.airdrop?.round ?? 1;
  const isRound2 = round === 2;

  const { project, loading, error, refetch } = useHackathonProjects({ documentId });

  React.useEffect(() => {
    if (!voteRanking) dispatch(fetchVoteRanking());
  }, [dispatch, voteRanking]);

  const rankingKey: keyof IVoteRanking = isRound2 ? 'round2' : 'round1';

  const round1EndsAt = React.useMemo(() => {
    const v = airdropInfo?.airdrop?.round1_ends_on;
    return v ? Date.parse(v) : null;
  }, [airdropInfo?.airdrop?.round1_ends_on]);

  const round2StartsAt = React.useMemo(() => {
    const v = airdropInfo?.airdrop?.round2_starts_on;
    return v ? Date.parse(v) : null;
  }, [airdropInfo?.airdrop?.round2_starts_on]);

  const needsLiveTime = !!round1EndsAt || !!round2StartsAt;
  const now = useNowMs(needsLiveTime, 1000);

  const isRound1Finished = round1EndsAt !== null && now >= round1EndsAt;
  const isRound2Started = round2StartsAt !== null && now >= round2StartsAt;

  const isBetweenRounds = isRound1Finished && !isRound2Started;
  const canVote = !isBetweenRounds;

  const rank = React.useMemo<number | undefined>(() => {
    if (!voteRanking || !project?.project_hash) return undefined;
    const list = voteRanking[rankingKey] ?? [];
    return list.find((v) => v.project_address === project.project_hash)?.position;
  }, [voteRanking, rankingKey, project?.project_hash]);

  const description = React.useMemo(() => {
    const raw = project?.extended_description;
    return raw ? raw.replace(/\\n/g, '\n') : '';
  }, [project?.extended_description]);

  const supporters = React.useMemo(() => {
    if (!project) return 0;
    return isRound2 ? (project.round2_votes ?? 0) : (project.round1_votes ?? 0);
  }, [project, isRound2]);

  return {
    airdropInfo,
    project,
    loading,
    error,
    refetch,

    round,
    isRound2,
    isRound1Finished,
    isRound2Started,
    isBetweenRounds,
    canVote,

    rank,
    description,
    supporters
  };
};
